import type { JobDetailsComplete, JobDetailsResponse } from '@/features/jobber/types'

const EDGE_FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/jobber-line-items`
const API_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY
const WORKER_API_TOKEN = import.meta.env.VITE_N8N_JOBBER_WORKER_TOKEN

if (!EDGE_FUNCTION_URL || !API_KEY || !WORKER_API_TOKEN) {
  console.error('❌ Missing required environment variables for Jobber service')
}

function convertJobIdToBase64(jobId: string): string {
  if (!jobId.startsWith('gid://')) {
    return jobId
  }
  return btoa(jobId)
}

function buildHeaders() {
  return {
    Authorization: `Bearer ${WORKER_API_TOKEN}`,
    ...(API_KEY && { apikey: API_KEY }),
    'Content-Type': 'application/json',
  }
}

export async function fetchJobLineItems(
  jobId: string,
  franchise: 'hamilton' | 'northyork' = 'hamilton'
): Promise<JobDetailsComplete> {
  try {
    const base64JobId = convertJobIdToBase64(jobId)
    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({
        jobId: base64JobId,
        franchise,
        forceRefresh: false,
      }),
    })

    if (!response.ok) {
      throw new Error(`Edge Function request failed: ${response.status} ${response.statusText}`)
    }

    const result: JobDetailsResponse = await response.json()

    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to fetch job details')
    }

    return result.data
  } catch (error) {
    console.error('Error fetching job line items:', error)
    throw error
  }
}

export async function getCachedJobDetails(
  jobId: string,
  franchise: 'hamilton' | 'northyork' = 'hamilton'
): Promise<JobDetailsComplete | null> {
  try {
    const base64JobId = convertJobIdToBase64(jobId)
    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({
        action: 'get_cached',
        jobId: base64JobId,
        franchise,
      }),
    })

    if (!response.ok) {
      return null
    }

    const result: JobDetailsResponse = await response.json()
    return result.data || null
  } catch (error) {
    console.warn('Error getting cached job details:', error)
    return null
  }
}

export async function refreshJobDetails(
  jobId: string,
  franchise: 'hamilton' | 'northyork' = 'hamilton'
): Promise<JobDetailsComplete> {
  try {
    const base64JobId = convertJobIdToBase64(jobId)
    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({
        jobId: base64JobId,
        franchise,
        forceRefresh: true,
      }),
    })

    if (!response.ok) {
      throw new Error(`Refresh request failed: ${response.status} ${response.statusText}`)
    }

    const result: JobDetailsResponse = await response.json()

    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to refresh job details')
    }

    return result.data
  } catch (error) {
    console.error('Error refreshing job details:', error)
    throw error
  }
}
